import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { BrandIcon } from '@/shared/components/BrandIcon';
import { Loader2, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { useLanguage } from '@/core/LanguageContext';

type ConfirmationState = 'loading' | 'success' | 'error' | 'already_confirmed';

const EmailConfirmation = () => {
  const [state, setState] = useState<ConfirmationState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // Supabase handles token_hash and type from the URL automatically via detectSessionInUrl
      // We just need to check if there's an active session after the redirect
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Email confirmation error:', error);
        setErrorMessage(error.message);
        setState('error');
        return;
      }

      if (session) {
        // User is now authenticated - email was confirmed
        setState('success');
      } else {
        // No session means the token may have already been used or is invalid
        // Check URL params for error
        const errorDescription = searchParams.get('error_description');
        if (errorDescription) {
          setErrorMessage(errorDescription);
          setState('error');
        } else {
          // Token might have already been used - user should try signing in
          setState('already_confirmed');
        }
      }
    };

    // Small delay to allow Supabase to process the URL hash
    const timer = setTimeout(handleEmailConfirmation, 500);
    return () => clearTimeout(timer);
  }, [searchParams]);

  const handleContinue = () => {
    navigate('/');
  };

  const handleGoToSignIn = () => {
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <BrandIcon className="h-9 w-9" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Factory Pilot</h1>
          </div>
        </div>

        <Card className="shadow-elevated">
          <CardHeader className="text-center">
            {state === 'loading' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                  <Loader2 className="h-7 w-7 animate-spin text-primary" />
                </div>
                <CardTitle>{t('auth.confirmation.verifying', 'Verifying your email')}</CardTitle>
                <CardDescription>
                  {t('auth.confirmation.pleaseWait', 'Please wait while we confirm your email address...')}
                </CardDescription>
              </>
            )}

            {state === 'success' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle className="text-green-600 dark:text-green-400">
                  {t('auth.confirmation.success', 'Email Confirmed!')}
                </CardTitle>
                <CardDescription>
                  {t('auth.confirmation.successDescription', 'Your email has been verified successfully. You can now access your account.')}
                </CardDescription>
              </>
            )}

            {state === 'error' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                  <XCircle className="h-7 w-7 text-destructive" />
                </div>
                <CardTitle className="text-destructive">
                  {t('auth.confirmation.error', 'Verification Failed')}
                </CardTitle>
                <CardDescription>
                  {errorMessage || t('auth.confirmation.errorDescription', 'We could not verify your email. The link may have expired or already been used.')}
                </CardDescription>
              </>
            )}

            {state === 'already_confirmed' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <Mail className="h-7 w-7 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle>
                  {t('auth.confirmation.alreadyConfirmed', 'Already Verified')}
                </CardTitle>
                <CardDescription>
                  {t('auth.confirmation.alreadyConfirmedDescription', 'Your email may have already been confirmed. Please sign in to continue.')}
                </CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {state === 'success' && (
              <Button 
                onClick={handleContinue} 
                className="w-full" 
                variant="industrial"
              >
                {t('auth.confirmation.continue', 'Continue to Dashboard')}
              </Button>
            )}

            {(state === 'error' || state === 'already_confirmed') && (
              <div className="space-y-3">
                <Button 
                  onClick={handleGoToSignIn} 
                  className="w-full" 
                  variant="industrial"
                >
                  {t('auth.confirmation.goToSignIn', 'Go to Sign In')}
                </Button>
                {state === 'error' && (
                  <p className="text-xs text-center text-muted-foreground">
                    {t('auth.confirmation.needHelp', 'If you continue to have issues, please contact support.')}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailConfirmation;
